import { z } from "zod";
import {
  CreateResult,
  CreateParams,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { v4 as randomUuid } from "uuid";

const _CreateAutoFormDataSchema = schemas.CreateDrivingSchoolAutoFormDto.pick({
  name: true,
  description: true,
  isForRegistration: true,
}).extend({ drivingSchoolId: z.string().min(1), pdfFile: z.unknown() });

const _DrivingSchoolAutoFormSchema = schemas.DrivingSchoolAutoFormDto.extend({
  drivingSchoolId: z.string(),
});
export type DrivingSchoolAutoForm = z.output<typeof _DrivingSchoolAutoFormSchema>;

class AutoFormsProvider extends AbstractDataProvider<DrivingSchoolAutoForm> {
  async getAllForDrivingSchool(drivingSchoolId: string, includeDeleted = false): Promise<Array<DrivingSchoolAutoForm>> {
    const serverClient = await getAuthenticatedServerClient();
    const dtos = await serverClient.getDrivingSchoolAutoForms({
      params: { id: drivingSchoolId },
      queries: includeDeleted ? { includeDeleted: true } : {},
    });
    return dtos.map(_dtoToRecord);
  }

  async getOne(_: string, { id }: GetOneParams<DrivingSchoolAutoForm>): Promise<GetOneResult<DrivingSchoolAutoForm>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getAutoForm({ params: { id } });
    return { data: _dtoToRecord(dto) };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<DrivingSchoolAutoForm>> {
    const { filter, sort, pagination, meta } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const records = await this.getAllForDrivingSchool(drivingSchoolId, meta?.includeDeleted === true);
    return applyPagination(applySort(applyFilter(records, clientSideFilter), sort), pagination);
  }

  async create(_resource: string, params: CreateParams): Promise<CreateResult<DrivingSchoolAutoForm>> {
    const {
      drivingSchoolId,
      name,
      description,
      isForRegistration,
      pdfFile: fileInput,
    } = _CreateAutoFormDataSchema.parse(params.data);
    const pdfFile = _extractPdfFile(fileInput);
    if (!pdfFile) {
      throw new Error("pdfFile is required");
    }
    const serverClient = await getAuthenticatedServerClient();
    const id = await serverClient.createAutoForm({
      name,
      description,
      isForRegistration,
      drivingSchoolId,
      idempotencyKey: randomUuid(),
    });
    await serverClient.attachPdfTemplateBaseFileToAutoForm({ pdf: pdfFile }, { params: { id } });
    const dto = await serverClient.getAutoForm({ params: { id } });
    return { data: _dtoToRecord(dto) };
  }

  async update(
    _resource: string,
    params: UpdateParams<DrivingSchoolAutoForm & { pdfFile?: unknown }>,
  ): Promise<UpdateResult<DrivingSchoolAutoForm>> {
    const { id, data } = params;
    const serverClient = await getAuthenticatedServerClient();
    const payload = schemas.UpdateDrivingSchoolAutoFormDto.parse(data);
    if (Object.keys(payload).length > 0) {
      await serverClient.updateAutoForm(payload, { params: { id } });
    }
    const pdfFile = _extractPdfFile(data.pdfFile);
    if (pdfFile) {
      await serverClient.attachPdfTemplateBaseFileToAutoForm({ pdf: pdfFile }, { params: { id } });
    }
    const dto = await serverClient.getAutoForm({ params: { id } });
    return { data: _dtoToRecord(dto) };
  }

  async delete(_resource: string, params: DeleteParams): Promise<DeleteResult> {
    const { id } = params;
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteAutoForm(undefined, { params: { id } });
    return { data: { id } };
  }
}

export const autoFormsProvider = new AutoFormsProvider();

function _dtoToRecord(dto: z.infer<typeof schemas.DrivingSchoolAutoFormDto>): DrivingSchoolAutoForm {
  return _DrivingSchoolAutoFormSchema.parse(dto);
}

function _extractPdfFile(value: unknown): File | undefined {
  if (!value) {
    return undefined;
  }
  if (value instanceof File) {
    return value;
  }
  if (Array.isArray(value)) {
    const firstValue = value[0];
    if (firstValue instanceof File) {
      return firstValue;
    }
    if (firstValue && typeof firstValue === "object" && "rawFile" in firstValue && firstValue.rawFile instanceof File) {
      return firstValue.rawFile;
    }
    return undefined;
  }
  if (typeof value === "object" && "rawFile" in value && value.rawFile instanceof File) {
    return value.rawFile;
  }
  return undefined;
}
